<template>
  <header ref="headerEl" class="sticky top-0 z-50 w-full">
    <div
      class="w-full border-b"
      :class="singleRowHeader
        ? 'border-slate-200/80 bg-white/90 backdrop-blur-md'
        : 'bg-default rounded-b-2xl border-default shadow-sm'"
    >
      <div v-if="topBannerEnabled" class="bg-brand-700 text-white text-sm hidden md:block">
        <div class="max-w-7xl mx-auto px-4 py-1.5 flex justify-between items-center">
          <span class="select-none">{{ topBannerText }}</span>
          <div class="flex items-center gap-4">
            <a :href="`tel:${site.phone}`" class="hover:underline flex items-center gap-1">
              <UIcon name="i-heroicons-phone" class="w-4 h-4" />
              {{ site.phone }}
            </a>
            <a :href="`mailto:${site.email}`" class="hover:underline flex items-center gap-1">
              <UIcon name="i-heroicons-envelope" class="w-4 h-4" />
              {{ site.email }}
            </a>
          </div>
        </div>
      </div>

      <div class="max-w-7xl mx-auto px-4">
        <div class="flex items-center justify-between h-16 gap-4">
          <NuxtLink :to="shopPath('/')" class="flex items-center gap-2 shrink-0">
            <SiteLogo
              :src="site.logoHeaderDesktop"
              :alt="site.name"
              :height="site.logoHeaderDesktopHeight"
              loading="eager"
              fetchpriority="high"
              image-class="hidden md:block w-auto max-w-full object-contain"
            />
            <SiteLogo
              :src="site.logoHeaderMobile"
              :alt="site.name"
              :height="site.logoHeaderMobileHeight"
              loading="eager"
              fetchpriority="high"
              image-class="md:hidden w-auto max-w-full object-contain"
            />
            <span
              v-if="headerBrand.show && headerBrand.text"
              class="font-bold text-highlighted hidden sm:block leading-tight"
              :style="{
                fontFamily: headerBrand.fontFamily,
                fontSize: `${headerBrand.fontSize}px`,
              }"
            >{{ headerBrand.text }}</span>
          </NuxtLink>

          <div
            v-if="megaHeader"
            class="relative hidden shrink-0 lg:block"
            @mouseenter="openMega"
            @mouseleave="scheduleMegaClose"
          >
            <button
              type="button"
              class="inline-flex items-center gap-1.5 rounded-full bg-brand-600 px-3.5 py-2 text-sm font-semibold text-[var(--ui-text-on-primary)]"
              :aria-expanded="megaOpen"
              @click="megaOpen = !megaOpen"
            >
              <UIcon name="i-heroicons-squares-2x2" class="size-4" />
              Categorii
              <UIcon name="i-heroicons-chevron-down" class="size-3.5" :class="megaOpen ? 'rotate-180' : ''" />
            </button>
          </div>

          <div class="hidden min-w-0 flex-1 lg:block lg:max-w-2xl">
            <SearchBar />
          </div>

          <div class="ml-auto flex items-center gap-1 sm:gap-2">
            <div class="lg:hidden shrink-0">
              <SearchBar variant="icon" />
            </div>
            <div v-if="sellPublic" class="hidden md:block">
              <ShopModeSwitch />
            </div>
            <ClientOnly>
              <UButton
                v-if="isAdmin"
                :to="shopPath('/admin')"
                variant="ghost"
                color="neutral"
                icon="i-heroicons-cog-6-tooth"
                aria-label="Panou administrare"
              />
            </ClientOnly>
            <UButton
              :to="shopPath('/cont/wishlist')"
              variant="ghost"
              color="neutral"
              icon="i-heroicons-heart"
              aria-label="Produse favorite"
            />
            <ClientOnly>
              <UButton
                variant="ghost"
                color="neutral"
                icon="i-heroicons-user"
                :to="isAuthenticated ? (isAdmin ? shopPath('/admin') : shopPath('/cont')) : shopPath('/cont/login')"
                aria-label="Cont"
              />
              <template #fallback>
                <UButton
                  variant="ghost"
                  color="neutral"
                  icon="i-heroicons-user"
                  :to="shopPath('/cont/login')"
                  aria-label="Cont"
                />
              </template>
            </ClientOnly>
            <span data-cart-target class="inline-flex">
            <UButton
              variant="ghost"
              color="neutral"
              icon="i-heroicons-shopping-cart"
              aria-label="Coș"
              :class="{ 'cart-bump': bumping }"
              @click="toggleDrawer"
            >
              <ClientOnly>
                <UBadge v-if="itemCount > 0" color="primary" variant="solid" size="xs" class="ml-1">
                  {{ itemCount }}
                </UBadge>
              </ClientOnly>
            </UButton>
            </span>
            <UButton
              class="lg:hidden"
              variant="ghost"
              color="neutral"
              icon="i-heroicons-bars-3"
              aria-label="Meniu"
              @click="mobileMenuOpen = !mobileMenuOpen"
            />
          </div>
        </div>

        <HeaderMenuBar :links="catalogNavLinks" />
      </div>

      <div v-if="!singleRowHeader && mobileMenuOpen" class="lg:hidden border-t border-default px-4 py-4 space-y-3">
        <ShopModeSwitch v-if="sellPublic" block />
        <nav class="flex flex-col gap-1">
          <UButton
            v-for="link in catalogNavLinks"
            :key="`${link.to}-${link.label}`"
            :to="shopPath(link.to)"
            variant="ghost"
            color="neutral"
            :label="link.label"
            block
            @click="mobileMenuOpen = false"
          />
        </nav>
      </div>
    </div>

    <div
      v-if="megaHeader && megaOpen"
      class="absolute left-0 right-0 top-full z-40 hidden px-4 pb-3 lg:block"
      @mouseenter="openMega"
      @mouseleave="scheduleMegaClose"
    >
      <HeaderMegaMenu
        layout="dropdown"
        :categories="menuCategories"
        @navigate="megaOpen = false"
      />
    </div>

    <Teleport to="body">
      <div
        v-if="headerStyle === 'compact' && mobileMenuOpen"
        class="fixed inset-0 z-[80] flex flex-col bg-white/55 backdrop-blur-2xl backdrop-saturate-150 lg:hidden"
      >
        <div class="flex justify-end px-4 pt-4">
          <button
            type="button"
            class="flex size-11 items-center justify-center rounded-full bg-white/70 text-slate-800 shadow-sm ring-1 ring-black/5"
            aria-label="Închide meniul"
            @click="mobileMenuOpen = false"
          >
            <UIcon name="i-heroicons-x-mark" class="size-6" />
          </button>
        </div>
        <nav class="flex flex-1 flex-col items-center justify-center gap-1 overflow-y-auto px-6">
          <ShopModeSwitch v-if="sellPublic" class="mb-6" @click="mobileMenuOpen = false" />
          <NuxtLink
            v-for="link in catalogNavLinks"
            :key="`glass-${link.to}-${link.label}`"
            :to="shopPath(link.to)"
            class="px-3 py-2 text-center text-3xl font-semibold tracking-tight"
            :class="isNavActive(link.to) ? 'text-brand-700' : 'text-slate-900/90'"
            @click="mobileMenuOpen = false"
          >
            {{ link.label }}
          </NuxtLink>
        </nav>
        <div
          v-if="site.phone || site.email"
          class="flex flex-col items-center gap-2 px-6 pb-10 text-sm text-slate-600"
        >
          <a v-if="site.phone" :href="`tel:${site.phone}`" class="inline-flex items-center gap-2">
            <UIcon name="i-heroicons-phone" class="size-4" />
            {{ site.phone }}
          </a>
          <a v-if="site.email" :href="`mailto:${site.email}`" class="inline-flex items-center gap-2">
            <UIcon name="i-heroicons-envelope" class="size-4" />
            {{ site.email }}
          </a>
        </div>
      </div>
      <div v-else-if="megaHeader && mobileMenuOpen" class="fixed inset-0 z-[80] lg:hidden">
        <button
          type="button"
          class="absolute inset-0 bg-slate-900/40"
          aria-label="Închide meniul"
          @click="mobileMenuOpen = false"
        />
        <div class="absolute inset-y-0 right-0 flex w-[min(100%,22rem)] flex-col bg-white shadow-2xl">
          <div class="flex items-center justify-between border-b border-slate-200 px-4 py-3">
            <p class="text-base font-semibold text-slate-900">Meniu</p>
            <UButton
              variant="ghost"
              color="neutral"
              icon="i-heroicons-x-mark"
              aria-label="Închide meniul"
              @click="mobileMenuOpen = false"
            />
          </div>
          <div class="flex-1 space-y-3 overflow-y-auto px-3 py-3">
            <HeaderMegaMenu
              v-if="megaHeader"
              layout="stack"
              :categories="menuCategories"
              @navigate="mobileMenuOpen = false"
            />
          <ShopModeSwitch v-if="sellPublic" block @click="mobileMenuOpen = false" />
          <nav class="space-y-1">
            <NuxtLink
              v-for="link in catalogNavLinks"
              :key="`drawer-${link.to}-${link.label}`"
              :to="shopPath(link.to)"
              class="flex items-center rounded-xl px-3 py-3 text-base font-medium"
              :class="isNavActive(link.to)
                ? 'bg-brand-50 text-brand-800'
                : 'text-slate-800 hover:bg-slate-50'"
              @click="mobileMenuOpen = false"
            >
              {{ link.label }}
            </NuxtLink>
          </nav>
          </div>
          <div v-if="site.phone || site.email" class="space-y-2 border-t border-slate-200 px-4 py-4 text-sm text-slate-600">
            <a v-if="site.phone" :href="`tel:${site.phone}`" class="flex items-center gap-2">
              <UIcon name="i-heroicons-phone" class="size-4" />
              {{ site.phone }}
            </a>
            <a v-if="site.email" :href="`mailto:${site.email}`" class="flex items-center gap-2">
              <UIcon name="i-heroicons-envelope" class="size-4" />
              {{ site.email }}
            </a>
          </div>
        </div>
      </div>
    </Teleport>
  </header>
</template>

<script setup lang="ts">
import { isPublicSellLink } from '~/composables/useSiteSettings'

const route = useRoute()
const shopPath = useShopPath()
const { site, topBannerEnabled, topBannerText, headerNavLinks, headerBrand, headerStyle, sellPublic } = useSiteSettings()
const catalogNavLinks = computed(() => headerNavLinks.value.filter((link) => !isPublicSellLink(link.to)))
const productsStore = useProductsStore()
const megaHeader = computed(() => headerStyle.value === 'mega')
const singleRowHeader = computed(() => headerStyle.value === 'compact' || megaHeader.value)
const menuCategories = computed(() => productsStore.categories)
const { isAuthenticated, isAdmin } = useAuth()
const { itemCount, toggleDrawer } = useCart()
const cartStore = useCartStore()

const headerEl = ref<HTMLElement | null>(null)
const mobileMenuOpen = ref(false)
const megaOpen = ref(false)
const bumping = ref(false)
let bumpTimer: ReturnType<typeof setTimeout> | null = null
let megaTimer: ReturnType<typeof setTimeout> | null = null

function openMega() {
  if (megaTimer) clearTimeout(megaTimer)
  megaOpen.value = true
}

function scheduleMegaClose() {
  if (megaTimer) clearTimeout(megaTimer)
  megaTimer = setTimeout(() => {
    megaOpen.value = false
  }, 220)
}

function navPath(to: string) {
  return shopPath(to).split('?')[0].replace(/\/+$/, '') || '/'
}

function isNavActive(to: string) {
  const target = navPath(to)
  const current = route.path.replace(/\/+$/, '') || '/'
  const home = navPath('/')
  if (target === home) return current === home
  return current === target || current.startsWith(`${target}/`)
}

watch(megaHeader, (on) => {
  if (!on || !import.meta.client || productsStore.categories.length) return
  void productsStore.fetchCategories()
}, { immediate: true })

watch(() => route.fullPath, () => {
  mobileMenuOpen.value = false
  megaOpen.value = false
})

watch(mobileMenuOpen, (open) => {
  if (!import.meta.client || !singleRowHeader.value) return
  document.body.style.overflow = open ? 'hidden' : ''
})

function onMenuKeydown(event: KeyboardEvent) {
  if (event.key !== 'Escape') return
  mobileMenuOpen.value = false
  megaOpen.value = false
}

function onPointerDown(event: PointerEvent) {
  if (!megaOpen.value) return
  const target = event.target
  if (target instanceof Node && headerEl.value?.contains(target)) return
  megaOpen.value = false
}

onMounted(() => {
  window.addEventListener('keydown', onMenuKeydown)
  document.addEventListener('pointerdown', onPointerDown)
})

watch(() => cartStore.bumpKey, () => {
  bumping.value = false
  void nextTick(() => {
    bumping.value = true
    if (bumpTimer) clearTimeout(bumpTimer)
    bumpTimer = setTimeout(() => {
      bumping.value = false
    }, 500)
  })
})

onBeforeUnmount(() => {
  if (bumpTimer) clearTimeout(bumpTimer)
  if (megaTimer) clearTimeout(megaTimer)
  window.removeEventListener('keydown', onMenuKeydown)
  document.removeEventListener('pointerdown', onPointerDown)
  if (import.meta.client) document.body.style.overflow = ''
})
</script>
